import {
  InformationOutlined,
  SentimentNegativeFilled,
  SentimentNeutralFilled,
  SentimentPositiveFilled,
} from "@aircall/icons";
import { Box, Flex, Link, Tooltip, Typography } from "@aircall/tractor";
import { useTranslation } from "react-i18next";

import { SectionLayout } from "../SectionLayout";

import { useSectionSentiment } from "./SectionSentiment.hooks";

import { Feedbacks } from "@/components/Feedbacks";
import { useCall } from "@/hooks/useCall/useCall";

import { SentimentType } from "@/graphql/generated/graphql";
import { PickContentProps, PickIconProps } from ".";

function PickIcon({ sentiment }: PickIconProps) {
  switch (sentiment) {
    case "POSITIVE":
      return <SentimentPositiveFilled size={16} />;
    case "NEGATIVE":
      return <SentimentNegativeFilled size={16} />;
    case "NEUTRAL":
      return <SentimentNeutralFilled size={16} />;
    default:
      return null;
  }
}

function PickContent({ sentiment }: PickContentProps) {
  const { t } = useTranslation();

  switch (sentiment) {
    case "POSITIVE":
      return (
        <Typography color="text-secondary" variant="bodySemiboldS">
          {t("customer_mood.positive")}
        </Typography>
      );
    case "NEGATIVE":
      return (
        <Typography color="text-secondary" variant="bodySemiboldS">
          {t("customer_mood.negative")}
        </Typography>
      );
    case "NEUTRAL":
      return (
        <Typography color="text-secondary" variant="bodySemiboldS">
          {t("customer_mood.neutral")}
        </Typography>
      );
    default:
      return null;
  }
}

export function SectionSentiment() {
  const { t } = useTranslation();

  const { call } = useCall();
  const sentiment = call?.overallSentiments?.get(SentimentType.EXTERNAL);

  const {
    onClickThumbUp: handleOnClickThumbUp,
    onClickThumbUpUpdated: handleOnClickThumbUpUpdated,
    onClickThumbDown: handleOnClickThumbDown,
    onClickThumbDownUpdated: handleOnClickThumbDownUpdated,
  } = useSectionSentiment();

  if (!sentiment) {
    return null;
  }

  return (
    <SectionLayout data-test="customer-mood-card">
      <Flex gap="xxs" alignItems="center">
        <Typography variant="bodySemiboldM">
          {t("customer_mood.title")}
        </Typography>
        <Tooltip
          align="center"
          side="top"
          content={
            <Typography color="white">
              {t("customer_mood.tooltip.content")}{" "}
              <Link href={t("customer_mood.tooltip.href_url")} target="_blank">
                {t("customer_mood.tooltip.href_title")}
              </Link>
            </Typography>
          }
        >
          <Box data-test="customer-mood-info">
            <InformationOutlined size={16} />
          </Box>
        </Tooltip>
      </Flex>
      <Flex gap="s" alignItems="center">
        <Flex gap="xxs" alignItems="center">
          <PickIcon sentiment={sentiment} />
          <PickContent sentiment={sentiment} />
        </Flex>
        <Feedbacks
          onClickThumbUp={handleOnClickThumbUp}
          onClickThumbDown={handleOnClickThumbDown}
          onClickThumbUpUpdated={handleOnClickThumbUpUpdated}
          onClickThumbDownUpdated={handleOnClickThumbDownUpdated}
        />
      </Flex>
    </SectionLayout>
  );
}
